import React, { useState } from 'react';
import { X, UserPlus, UserMinus, Crown } from 'lucide-react';

interface ChannelMember {
  id: string;
  name: string;
  email: string;
  profile_image_url?: string;
}

interface ChannelMembersModalProps {
  show: boolean;
  channelName: string;
  channelType: 'public' | 'private' | 'direct';
  members: ChannelMember[];
  allWorkspaceUsers: ChannelMember[];
  currentUserId: string;
  channelCreatorId?: string;
  isChannelCreator: boolean;
  onClose: () => void;
  onInviteMember: (userId: string) => Promise<void>;
  onRemoveMember: (userId: string) => Promise<void>;
}

const ChannelMembersModal: React.FC<ChannelMembersModalProps> = ({
  show,
  channelName,
  channelType,
  members,
  allWorkspaceUsers,
  currentUserId,
  channelCreatorId,
  isChannelCreator,
  onClose,
  onInviteMember,
  onRemoveMember
}) => {
  const [showInviteList, setShowInviteList] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  if (!show) return null;

  // 현재 채널에 없는 사용자들
  const nonMembers = allWorkspaceUsers.filter(
    user => !members.find(m => m.id === user.id)
  );

  // 검색 필터링
  const filteredNonMembers = nonMembers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInvite = async (userId: string) => {
    setIsInviting(true);
    try {
      await onInviteMember(userId);
      setSearchTerm('');
    } catch (error) {
      console.error('Failed to invite member:', error);
      alert('멤버 초대에 실패했습니다.');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = async (userId: string) => {
    if (!window.confirm('정말 이 멤버를 추방하시겠습니까?')) return;

    setIsRemoving(true);
    try {
      await onRemoveMember(userId);
    } catch (error) {
      console.error('Failed to remove member:', error);
      alert('멤버 추방에 실패했습니다.');
    } finally {
      setIsRemoving(false);
    }
  };

  const canManageMembers = channelType === 'private' && isChannelCreator;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              #{channelName} 멤버
            </h2>
            <p className="text-sm text-gray-500">{members.length}명</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Invite Button (Private channels only) */}
        {canManageMembers && nonMembers.length > 0 && (
          <div className="p-4 border-b bg-gray-50">
            <button
              onClick={() => setShowInviteList(!showInviteList)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              멤버 초대하기
            </button>
          </div>
        )}

        {/* Invite List */}
        {showInviteList && canManageMembers && (
          <div className="p-4 border-b bg-gray-50">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="이름 또는 이메일로 검색..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
            />
            <div className="max-h-40 overflow-y-auto space-y-1">
              {filteredNonMembers.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">
                  {searchTerm ? '검색 결과가 없습니다.' : '초대할 수 있는 멤버가 없습니다.'}
                </p>
              ) : (
                filteredNonMembers.map(user => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      {user.profile_image_url ? (
                        <img
                          src={user.profile_image_url}
                          alt={user.name}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleInvite(user.id)}
                      disabled={isInviting}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      초대
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {members.map(member => (
            <div
              key={member.id}
              className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                {member.profile_image_url ? (
                  <img
                    src={member.profile_image_url}
                    alt={member.name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{member.name}</p>
                    {member.id === channelCreatorId && (
                      <span title="채널 생성자">
                        <Crown className="w-4 h-4 text-yellow-500" />
                      </span>
                    )}
                    {member.id === currentUserId && (
                      <span className="text-xs text-gray-500">(나)</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{member.email}</p>
                </div>
              </div>

              {/* Remove button (only for channel creator, can't remove self or creator) */}
              {canManageMembers &&
               member.id !== currentUserId &&
               member.id !== channelCreatorId && (
                <button
                  onClick={() => handleRemove(member.id)}
                  disabled={isRemoving}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  title="추방하기"
                >
                  <UserMinus className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChannelMembersModal;
